import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { SITE } from "../data/site";

const NAV = [
  { label: "Journey", to: "/journey" },
  { label: "Training", to: "/training" },
  { label: "Workshops", to: "/workshops" },
  { label: "Kids", to: "/kids" },
  { label: "Events", to: "/events" },
  { label: "Online", to: "/online" },
  { label: "Find a Class", to: "/schedule" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={SITE.logo} alt="USDD Academy" className="h-12 w-12 object-contain drop-shadow-[0_0_12px_rgba(157,78,221,0.45)] transition-transform group-hover:scale-110" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-lg uppercase tracking-tight">USDD</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Academy</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors relative group ${
                  isActive ? "text-brand-yellow" : "text-zinc-300 hover:text-brand-yellow"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[2px] bg-brand-yellow transition-all ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/journey"
            data-testid="header-start-journey-btn"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-yellow text-black font-bold text-sm uppercase tracking-wide hover:bg-brand-yellowHover transition-all hover:scale-105 glow-yellow"
          >
            Start Your Journey
            <ArrowRight size={14} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-white hover:text-brand-yellow"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-nav" className="lg:hidden bg-black/95 border-t border-white/10 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-base font-semibold text-zinc-200 hover:text-brand-yellow"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/journey"
              onClick={() => setOpen(false)}
              data-testid="mobile-start-journey-btn"
              className="mt-2 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-bold text-sm uppercase"
            >
              Start Your Journey <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
